/*
  Panneau de navigation mobile.

  Le balisage sert d'abord sans script : sous 990 px, le panneau est un bloc de
  liens empilés sous la barre. Ce fichier ne s'active que s'il trouve ses
  ancrages, pose alors `nav-enhanced` sur `<html>` et transforme le bloc en
  tiroir latéral.

  Exigences d'accessibilité (design.md §6.2 et §10) : fermeture par Échap,
  focus contenu dans le tiroir, retour du focus sur le bouton d'ouverture,
  contenu sous-jacent retiré de l'arbre d'accessibilité.
*/
(() => {
  const root = document.documentElement;
  const toggle = document.querySelector('[data-nav-toggle]');
  const panel = document.querySelector('[data-nav-panel]');
  const scrim = document.querySelector('[data-nav-scrim]');
  if (!toggle || !panel || !scrim) return;

  root.classList.add('nav-enhanced');
  toggle.hidden = false;
  scrim.hidden = false;

  const FOCUSABLE =
    'a[href], button:not([disabled]), select, input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])';
  /* Seuil unique, tenu avec la feuille de style de l'en-tête. */
  const desktop = window.matchMedia('(min-width: 990px)');
  const productItems = [...panel.querySelectorAll('.header__item--products')];
  let open = false;

  const focusable = () =>
    [...panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);

  const setProductsOpen = (item, next) => {
    for (const candidate of productItems) {
      const active = candidate === item && next;
      candidate.toggleAttribute('data-products-open', active);
      candidate
        .querySelector('[data-products-toggle]')
        ?.setAttribute('aria-expanded', String(active));
    }
  };

  const closeProductsMenus = () => {
    for (const item of productItems) setProductsOpen(item, false);
  };

  for (const item of productItems) {
    const productToggle = item.querySelector('[data-products-toggle]');
    productToggle?.addEventListener('click', (event) => {
      event.stopPropagation();
      setProductsOpen(item, !item.hasAttribute('data-products-open'));
    });
  }

  document.addEventListener('click', (event) => {
    if (!event.target.closest('.header__item--products')) closeProductsMenus();
  });

  /*
    Le verrou de défilement compense la barre de défilement disparue, sinon la
    page saute latéralement à l'ouverture.
  */
  const lockScroll = () => {
    const gap = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (gap > 0) document.body.style.paddingRight = `${gap}px`;
  };

  const unlockScroll = () => {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  };

  const setOpen = (next) => {
    if (next === open) return;
    open = next;

    toggle.setAttribute('aria-expanded', String(open));
    root.classList.toggle('nav-open', open);

    if (open) {
      panel.setAttribute('data-nav-open', '');
      scrim.setAttribute('data-nav-open', '');
      lockScroll();
      const first = focusable()[0];
      if (first) first.focus();
    } else {
      closeProductsMenus();
      panel.removeAttribute('data-nav-open');
      scrim.removeAttribute('data-nav-open');
      unlockScroll();
      /*
        Le bouton est le seul point d'entrée du panneau : c'est donc toujours
        le point de retour attendu, y compris après un clic qui n'aurait pas
        déplacé le focus à l'ouverture.
      */
      toggle.focus();
    }
  };

  toggle.addEventListener('click', () => setOpen(!open));
  scrim.addEventListener('click', () => setOpen(false));

  panel.addEventListener('click', (event) => {
    if (event.target.closest('[data-nav-close]')) setOpen(false);
    /* Un lien suivi ferme le tiroir : la page suivante ne doit pas l'hériter. */
    else if (event.target.closest('a[href]')) {
      closeProductsMenus();
      setOpen(false);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      const productItem = productItems.find((item) =>
        item.hasAttribute('data-products-open')
      );
      if (productItem) {
        event.preventDefault();
        setProductsOpen(productItem, false);
        productItem.querySelector('[data-products-toggle]')?.focus();
        return;
      }
    }

    if (!open) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      return;
    }

    if (event.key !== 'Tab') return;

    const items = focusable();
    if (items.length === 0) return;

    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || !panel.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* Passer au bureau pendant que le tiroir est ouvert doit tout remettre à plat. */
  const onBreakpoint = () => {
    if (desktop.matches) setOpen(false);
  };

  if (typeof desktop.addEventListener === 'function') {
    desktop.addEventListener('change', onBreakpoint);
  } else {
    desktop.addListener(onBreakpoint);
  }
})();
